/**
 * Data Access Layer — Repository Pattern
 *
 * Single abstraction for ALL data persistence in Meester djay.
 * Currently backed by localStorage; designed for painless backend migration.
 *
 * USAGE:
 *   import { dataLayer } from '../data/dataLayer'
 *   const group = await dataLayer.getGroup()
 *   await dataLayer.setGroup('6')
 *
 * DESIGN:
 * - ALL functions are async (even in localStorage mode)
 * - USE_BACKEND flag switches between localStorage and API calls
 * - Each domain has its own section with clear function signatures
 * - Auth is a special case (password hashing must move server-side)
 *
 * MIGRATION STATUS: See docs/DATALAYER_MIGRATION.md
 *
 * @created 1 april 2026
 */

import { safeStorageWrite } from '../utils/storageQuota'
import { debug, warn, error as logError, safeJsonParse } from '../utils/logger'
import { STORAGE_KEY } from '../config/constants'

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * When API_BASE is set (non-empty string), the dataLayer switches to backend mode.
 * When empty/undefined, it uses localStorage.
 */
const API_BASE = '' // e.g. 'https://api.meesterdjay.nl/v1'

const USE_BACKEND = Boolean(API_BASE)

// ─── Internal Helpers ─────────────────────────────────────────────────────────

/** Generic localStorage read with JSON parse */
function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return parsed ?? fallback
  } catch {
    return fallback
  }
}

/** Generic localStorage write with JSON stringify */
function writeJson(key: string, value: unknown): boolean {
  return safeStorageWrite(key, JSON.stringify(value))
}

/** Generic localStorage remove */
function removeKey(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // silent
  }
}

/**
 * Generic API fetch helper (for future backend mode).
 * Not used yet — placeholder for Fase 3.
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add auth token header when backend is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    ...options,
  })
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

// ═══════════════════════════════════════════════════════════════════════════════
// D1: App Preferences (group, period)
// Source: src/app/store.ts lines 375-428
// ═══════════════════════════════════════════════════════════════════════════════

async function getGroup(): Promise<string | null> {
  if (USE_BACKEND) {
    // TODO: return apiFetch<string>('/preferences/group')
    throw new Error('Backend not implemented')
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_group`)
    if (!stored) return null
    return JSON.parse(stored)
  } catch {
    return null
  }
}

async function setGroup(group: string): Promise<void> {
  if (USE_BACKEND) {
    // TODO: await apiFetch('/preferences/group', { method: 'PUT', body: JSON.stringify({ group }) })
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(`${STORAGE_KEY}_group`, JSON.stringify(group))
  } catch {
    /* quota */
  }
}

async function getPeriod(): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_period`)
    if (!stored) return null
    return JSON.parse(stored)
  } catch {
    return null
  }
}

async function setPeriod(period: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(`${STORAGE_KEY}_period`, JSON.stringify(period))
  } catch {
    /* quota */
  }
}

async function clearPeriod(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  removeKey(`${STORAGE_KEY}_period`)
}

// ═══════════════════════════════════════════════════════════════════════════════
// D2: Multi-Account State (load/save entire app state)
// Source: src/accounts/logic/migration.ts
// Note: Migration/normalization logic stays in migration.ts.
//       dataLayer only handles the raw read/write.
// ═══════════════════════════════════════════════════════════════════════════════

async function loadMultiAccountState(): Promise<string | null> {
  if (USE_BACKEND) {
    // TODO: return apiFetch<string>('/state')
    throw new Error('Backend not implemented')
  }
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

async function saveMultiAccountState(
  serializedState: string,
): Promise<boolean> {
  if (USE_BACKEND) {
    // TODO: await apiFetch('/state', { method: 'PUT', body: serializedState })
    throw new Error('Backend not implemented')
  }
  return safeStorageWrite(STORAGE_KEY, serializedState)
}

// ═══════════════════════════════════════════════════════════════════════════════
// D3: Assessment Storage (blueprints, attempts, reports)
// Source: src/assessment/utils/assessmentStorage.ts
// ═══════════════════════════════════════════════════════════════════════════════

const BLUEPRINTS_KEY = `${STORAGE_KEY}_assessment_blueprints`
const ATTEMPTS_KEY = `${STORAGE_KEY}_assessment_attempts`
const REPORTS_KEY = `${STORAGE_KEY}_assessment_reports`

// ── Blueprints ──

async function getAssessmentBlueprint(
  id: string,
): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    BLUEPRINTS_KEY,
    {},
  )
  return all[id] ?? null
}

async function getAllAssessmentBlueprints(): Promise<
  Record<string, Record<string, unknown>>
> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, Record<string, unknown>>>(BLUEPRINTS_KEY, {})
}

async function saveAssessmentBlueprint(
  id: string,
  blueprint: Record<string, unknown>,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    BLUEPRINTS_KEY,
    {},
  )
  all[id] = blueprint
  writeJson(BLUEPRINTS_KEY, all)
}

// ── Attempts ──

async function getAssessmentAttempt(
  id: string,
): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    ATTEMPTS_KEY,
    {},
  )
  return all[id] ?? null
}

async function getAllAssessmentAttempts(): Promise<
  Record<string, Record<string, unknown>>
> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, Record<string, unknown>>>(ATTEMPTS_KEY, {})
}

async function saveAssessmentAttempt(
  id: string,
  attempt: Record<string, unknown>,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    ATTEMPTS_KEY,
    {},
  )
  all[id] = attempt
  writeJson(ATTEMPTS_KEY, all)
}

// ── Reports ──

async function getAssessmentReport(
  attemptId: string,
): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(REPORTS_KEY, {})
  return all[attemptId] ?? null
}

async function getAllAssessmentReports(): Promise<
  Record<string, Record<string, unknown>>
> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, Record<string, unknown>>>(REPORTS_KEY, {})
}

async function saveAssessmentReport(
  attemptId: string,
  report: Record<string, unknown>,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(REPORTS_KEY, {})
  all[attemptId] = report
  writeJson(REPORTS_KEY, all)
}

// ── Assessment CRUD helpers ──

async function deleteAssessmentAttempt(id: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  // Remove from attempts
  const attempts = readJson<Record<string, unknown>>(ATTEMPTS_KEY, {})
  delete attempts[id]
  writeJson(ATTEMPTS_KEY, attempts)

  // Remove associated report
  const reports = readJson<Record<string, unknown>>(REPORTS_KEY, {})
  delete reports[id]
  writeJson(REPORTS_KEY, reports)
}

async function clearAllAssessmentData(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  removeKey(BLUEPRINTS_KEY)
  removeKey(ATTEMPTS_KEY)
  removeKey(REPORTS_KEY)
}

// ═══════════════════════════════════════════════════════════════════════════════
// D4: Assessment Session Recovery
// Source: src/assessment/utils/assessmentSessionRecovery.ts
// ═══════════════════════════════════════════════════════════════════════════════

const SESSION_RECOVERY_KEY = `${STORAGE_KEY}_assessment_session`

async function getAssessmentSession(): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, unknown> | null>(SESSION_RECOVERY_KEY, null)
}

async function saveAssessmentSession(
  session: Record<string, unknown>,
): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return safeStorageWrite(SESSION_RECOVERY_KEY, JSON.stringify(session))
}

async function clearAssessmentSession(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  removeKey(SESSION_RECOVERY_KEY)
}

// ═══════════════════════════════════════════════════════════════════════════════
// D5: Learner Preferences (sound, reducedMotion, dailyGoal)
// Source: src/components/settings/SettingsPage.tsx
// ═══════════════════════════════════════════════════════════════════════════════

const LEARNER_PREFS_KEY = 'meester-djay-learner-prefs'

interface LearnerPreferences {
  sound: boolean
  reducedMotion: boolean
  dailyGoal: 5 | 10 | 20
}

const DEFAULT_LEARNER_PREFS: LearnerPreferences = {
  sound: true,
  reducedMotion: false,
  dailyGoal: 10,
}

async function getLearnerPreferences(): Promise<LearnerPreferences> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const raw = localStorage.getItem(LEARNER_PREFS_KEY)
    if (!raw) return { ...DEFAULT_LEARNER_PREFS }
    const parsed = JSON.parse(raw)
    return {
      sound: parsed.sound ?? true,
      reducedMotion: parsed.reducedMotion ?? false,
      dailyGoal: [5, 10, 20].includes(parsed.dailyGoal) ? parsed.dailyGoal : 10,
    }
  } catch {
    return { ...DEFAULT_LEARNER_PREFS }
  }
}

async function saveLearnerPreferences(
  prefs: LearnerPreferences,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(LEARNER_PREFS_KEY, JSON.stringify(prefs))
  } catch {
    // silent
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// D6: Governance Config
// Source: src/governance/pipeline/governanceConfigStore.ts
// ═══════════════════════════════════════════════════════════════════════════════

const GOVERNANCE_CONFIG_KEY = 'meester_djay_governance_config_v1'

async function getGovernanceConfig(): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const raw = localStorage.getItem(GOVERNANCE_CONFIG_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

async function saveGovernanceConfig(
  config: Record<string, unknown>,
): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return safeStorageWrite(GOVERNANCE_CONFIG_KEY, JSON.stringify(config))
}

async function resetGovernanceConfig(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  removeKey(GOVERNANCE_CONFIG_KEY)
}

// ═══════════════════════════════════════════════════════════════════════════════
// D7: Djay Selection (mascot variant per account)
// Source: src/mascot/useDjaySelection.ts
// ═══════════════════════════════════════════════════════════════════════════════

const DJAY_VARIANT_PREFIX = 'meester-djay-variant-'

async function getDjayVariant(
  accountId: string | null,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_VARIANT_PREFIX}${accountId}`
      : `${DJAY_VARIANT_PREFIX}global`
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

async function saveDjayVariant(
  accountId: string | null,
  variant: string | null,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const key = accountId
      ? `${DJAY_VARIANT_PREFIX}${accountId}`
      : `${DJAY_VARIANT_PREFIX}global`
    if (!variant || variant === 'default') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, variant)
    }
  } catch {
    // silent
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// D8: Admin Settings
// Source: src/portals/admin/pages/SettingsPage.tsx
// ═══════════════════════════════════════════════════════════════════════════════

const ADMIN_SETTINGS_KEY = 'meester-djay-admin-settings'

async function getAdminSettings(): Promise<Record<string, unknown> | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const raw = localStorage.getItem(ADMIN_SETTINGS_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

async function saveAdminSettings(
  settings: Record<string, unknown>,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(ADMIN_SETTINGS_KEY, JSON.stringify(settings))
  } catch {
    // silent
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// D9: Oefenlab UI State
// Source: src/learningLab/LearningLabPage.tsx
// ═══════════════════════════════════════════════════════════════════════════════

const OEFENLAB_WELCOME_KEY = 'oefenlab_welcome_dismissed'
const FLUENCY_INTRO_KEY = 'fluency_intro_seen'

async function getOefenlabWelcomeDismissed(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    return localStorage.getItem(OEFENLAB_WELCOME_KEY) === '1'
  } catch {
    return false
  }
}

async function setOefenlabWelcomeDismissed(dismissed: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    if (dismissed) {
      localStorage.setItem(OEFENLAB_WELCOME_KEY, '1')
    } else {
      localStorage.removeItem(OEFENLAB_WELCOME_KEY)
    }
  } catch {
    // silent
  }
}

async function getFluencyIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    return localStorage.getItem(FLUENCY_INTRO_KEY) === '1'
  } catch {
    return false
  }
}

async function setFluencyIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    if (seen) {
      localStorage.setItem(FLUENCY_INTRO_KEY, '1')
    } else {
      localStorage.removeItem(FLUENCY_INTRO_KEY)
    }
  } catch {
    // silent
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Public API — Single export object
// ═══════════════════════════════════════════════════════════════════════════════

export const dataLayer = {
  // Config
  isBackendMode: USE_BACKEND,

  // D1: App Preferences
  getGroup,
  setGroup,
  getPeriod,
  setPeriod,
  clearPeriod,

  // D2: Multi-Account State
  loadMultiAccountState,
  saveMultiAccountState,

  // D3: Assessment Storage
  getAssessmentBlueprint,
  getAllAssessmentBlueprints,
  saveAssessmentBlueprint,
  getAssessmentAttempt,
  getAllAssessmentAttempts,
  saveAssessmentAttempt,
  getAssessmentReport,
  getAllAssessmentReports,
  saveAssessmentReport,
  deleteAssessmentAttempt,
  clearAllAssessmentData,

  // D4: Assessment Session Recovery
  getAssessmentSession,
  saveAssessmentSession,
  clearAssessmentSession,

  // D5: Learner Preferences
  getLearnerPreferences,
  saveLearnerPreferences,

  // D6: Governance Config
  getGovernanceConfig,
  saveGovernanceConfig,
  resetGovernanceConfig,

  // D7: Djay Selection
  getDjayVariant,
  saveDjayVariant,

  // D8: Admin Settings
  getAdminSettings,
  saveAdminSettings,

  // D9: Oefenlab UI State
  getOefenlabWelcomeDismissed,
  setOefenlabWelcomeDismissed,
  getFluencyIntroSeen,
  setFluencyIntroSeen,
} as const

// Also export types for consumers
export type { LearnerPreferences }
export type DataLayer = typeof dataLayer
